import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "@/components/ui/button";
import { useSession } from "@/services/auth0/session";
import { Link } from "react-router";
import {
  ChevronRightIcon,
  Loader2,
  Calculator,
  Clock,
  MapPin,
  Truck,
  Star,
  LogIn,
  UserPlus,
  RefreshCw,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { QuoteForm } from "./quote-form";
import { Card } from "@/components/ui/card";
import { InteractiveMap } from "./interactive-map";
import { useState, useCallback } from "react";

export default function Home() {
  const { loginWithRedirect } = useAuth0();
  const { user, isLoading, customer } = useSession();
  const [selectedLocations, setSelectedLocations] = useState<{
    from: string | null;
    to: string | null;
  }>({ from: null, to: null });
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const isNewCustomer = !customer?.billing?.id;

  const onLogin = () => {
    void loginWithRedirect();
  };

  const onCreateAccount = () => {
    void loginWithRedirect({
      authorizationParams: {
        screen_hint: "signup",
      },
    });
  };

  // Handler for when locations are selected on the map
  const handleLocationSelect = useCallback(
    (from: string | null, to: string | null) => {
      // Only update if values have changed to prevent unnecessary re-renders
      setSelectedLocations({ from, to });

      // If both locations are selected, open the sheet
      if (from && to) {
        setIsSheetOpen(true);
      }
    },
    []
  );

  // Handler for the Get Quote button
  const handleGetQuote = useCallback(() => {
    setIsSheetOpen(true);
  }, []);

  // Handler for closing the sheet
  const handleSheetClose = useCallback(() => {
    setIsSheetOpen(false);
  }, []);

  // Handler for resetting location selections
  const handleResetLocations = useCallback(() => {
    setSelectedLocations({ from: null, to: null });
  }, []);

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="flex flex-col md:flex-row items-start justify-between gap-8 pb-8 sm:pb-12 pt-4 sm:pt-8">
          <div className="md:w-1/2 space-y-4 sm:space-y-6 w-full">
            <div className="inline-block px-3 py-1 sm:px-4 sm:py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full text-white text-xs sm:text-sm font-medium mb-2 sm:mt-12">
              <MapPin className="inline-block w-3 h-3 sm:w-4 sm:h-4 mr-1" />{" "}
              Toronto's Fastest Last Mile Delivery
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-black dark:text-white font-sans tracking-tight">
              Same-Day Delivery,{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 via-violet-500 to-pink-500">
                Too EZ
              </span>
              .
            </h1>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-xl">
              We deliver your packages across Toronto in hours, not days. Fast,
              reliable, and cost-effective last mile delivery solutions for your
              business.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 w-full">
              {isLoading ? (
                <Button
                  size="lg"
                  className="p-4 sm:p-5 text-sm sm:text-base w-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                  variant="default"
                  disabled
                >
                  <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                  <span>Loading...</span>
                </Button>
              ) : !user ? (
                <>
                  <Button
                    variant="default"
                    size="lg"
                    className="p-4 sm:p-5 text-sm sm:text-base bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all duration-300 w-full sm:w-auto"
                    onClick={onLogin}
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Login
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="p-4 sm:p-5 text-sm sm:text-base w-full sm:w-auto"
                    onClick={onCreateAccount}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Create Account
                  </Button>
                </>
              ) : (
                <Button
                  asChild
                  size="lg"
                  variant="default"
                  className="p-4 sm:p-5 text-sm sm:text-base bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all duration-300"
                >
                  <Link
                    to={isNewCustomer ? "/onboarding/step-1" : "/dashboard"}
                    className="flex items-center justify-center gap-2 sm:gap-3"
                  >
                    {user.picture && (
                      <img
                        src={user.picture}
                        alt="Profile"
                        className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex-shrink-0"
                      />
                    )}
                    <span>
                      {isNewCustomer
                        ? "Complete Onboarding"
                        : "Go to Dashboard"}
                    </span>
                    <ChevronRightIcon className="w-4 h-4 flex-shrink-0" />
                  </Link>
                </Button>
              )}
              <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                <SheetTrigger asChild>
                  <Button
                    size="lg"
                    variant="outline"
                    className="p-4 sm:p-5 text-sm sm:text-base w-full sm:w-auto"
                    onClick={handleGetQuote}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    <span className="sm:inline">Get Shipping Quote</span>
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="bottom"
                  className="max-h-[80vh] overflow-y-auto"
                  onCloseAutoFocus={handleSheetClose}
                >
                  <div className="pt-6 sm:pt-8 flex flex-col lg:flex-row gap-4 overflow-y-auto">
                    <QuoteForm selectedLocations={selectedLocations} />
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <div className="flex -space-x-2">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-xs font-bold">
                  JD
                </div>
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-pink-100 flex items-center justify-center text-pink-600 text-xs font-bold">
                  KT
                </div>
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">
                  MS
                </div>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                <span className="font-medium">100+</span> businesses trust TooEZ
                Delivery in Toronto
              </p>
            </div>
          </div>

          <div className="md:w-1/2 mt-6 sm:mt-8 md:mt-0 w-full md:self-start">
            <div className="relative mt-8">
              <InteractiveMap onLocationSelect={handleLocationSelect} />

              <div className="-mt-8 flex justify-end">
                {selectedLocations.from ? (
                  <div className="inline-flex items-center bg-white rounded-lg shadow-sm px-3 py-2 border border-gray-100">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-600 flex items-center justify-center">
                        <span className="text-xs font-medium text-white">
                          F
                        </span>
                      </div>
                      <span className="ml-1.5 font-medium">
                        {selectedLocations.from}
                      </span>
                    </div>

                    {selectedLocations.to && (
                      <>
                        <div className="mx-2 text-gray-300">|</div>
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-pink-600 flex items-center justify-center">
                            <span className="text-xs font-medium text-white">
                              T
                            </span>
                          </div>
                          <span className="ml-1.5 font-medium">
                            {selectedLocations.to}
                          </span>
                        </div>
                      </>
                    )}

                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-2 h-6 p-0 w-6 rounded-full hover:bg-gray-100"
                      onClick={handleResetLocations}
                      title="Reset selections"
                    >
                      <RefreshCw className="w-3.5 h-3.5 text-gray-500" />
                    </Button>
                  </div>
                ) : (
                  <div className="inline-flex items-center bg-white/95 backdrop-blur-sm rounded-full shadow-md px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-100 transition-all hover:bg-white hover:shadow-lg">
                    <MapPin className="w-4 h-4 text-purple-600 mr-2 animate-pulse" />
                    Want to get a quote? Click to select from and to locations
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="py-8 sm:py-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-10">
            Why Choose TooEZ Delivery in Toronto?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            <Card className="p-4 sm:p-6 hover:shadow-lg transition-all duration-300 border-t-4 border-purple-500">
              <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-purple-500 mb-3 sm:mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold mb-2">
                Same-Day Delivery
              </h3>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                Get your packages delivered within hours, not days, across
                Toronto and GTA.
              </p>
            </Card>

            <Card className="p-4 sm:p-6 hover:shadow-lg transition-all duration-300 border-t-4 border-pink-500">
              <Truck className="w-8 h-8 sm:w-10 sm:h-10 text-pink-500 mb-3 sm:mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold mb-2">
                Last Mile Experts
              </h3>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                Specialized in last mile logistics with efficient routes and
                professional drivers.
              </p>
            </Card>

            <Card className="p-4 sm:p-6 hover:shadow-lg transition-all duration-300 border-t-4 border-blue-500">
              <Star className="w-8 h-8 sm:w-10 sm:h-10 text-blue-500 mb-3 sm:mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold mb-2">
                Reliable Service
              </h3>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                99.7% on-time delivery rate with real-time tracking and delivery
                confirmations.
              </p>
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-8 sm:py-12">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 shadow-xl">
            <div className="text-center">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:mb-4">
                Ready to simplify your Toronto deliveries?
              </h2>
              <p className="text-sm sm:text-base text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto">
                Join hundreds of businesses that trust TooEZ Delivery for their
                same-day delivery needs in Toronto.
              </p>

              <div className="flex justify-center">
                <Button
                  size="lg"
                  className="p-4 sm:p-5 text-sm sm:text-base bg-white text-purple-700 hover:bg-gray-100 w-full sm:w-auto"
                  onClick={onCreateAccount}
                >
                  <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Start Your Free Account
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
